import { CHAT_WIDGET_DIV_ID, CONSENT_BLACKBAR_DIV_ID, BANNER_DIV_ID, IMMERSIVE_SEARCH_PORTAL_ID } from 'constants/HTMLAttributes';
import { useRef, useEffect } from 'react';

// Array of element IDs to ignore when handling outside clicks
const IGNORED_ELEMENT_IDS = [CHAT_WIDGET_DIV_ID, CONSENT_BLACKBAR_DIV_ID, BANNER_DIV_ID, IMMERSIVE_SEARCH_PORTAL_ID];

// MUI renders menus, selects, autocompletes, dialogs, and tooltips into a portal on
// document.body — outside the guarded element's DOM subtree. A click inside one of those
// surfaces must NOT count as an "outside" click, otherwise interacting with a dropdown that
// lives inside a panel would close the panel itself.
const IGNORED_PORTAL_SELECTOR = [
  '.MuiPopover-root',
  '.MuiModal-root',
  '.MuiPopper-root',
  '.MuiAutocomplete-popper',
  '[role="listbox"]',
  '[role="option"]',
  '[role="presentation"]',
  // AreaPageV2 `RailMenu`'s mobile fullscreen sheet — portaled to `document.body`
  // so it isn't a DOM descendant of the trigger's guarded wrapper.
  '[data-testid="AreaPageV2-Rail-MenuFlyout"]',
].join(', ');

export default function useOutsideClickHandler<T extends HTMLElement>(callback: (() => void) | undefined) {
  const element = useRef<T>(null);



  const handler = (event: MouseEvent) => {
    const target = event.target as HTMLElement | null;

    // Check if the click was inside any of the ignored elements
    const isClickedIgnoredElement = IGNORED_ELEMENT_IDS.some(id => 
      document.getElementById(id)?.contains(target)
    );

    // A click that lands inside a MUI portal surface (dropdown menu, select list, dialog…).
    const isClickedPortalSurface = Boolean(target?.closest?.(IGNORED_PORTAL_SELECTOR));

    // MUI unmounts its menu/select portal the moment an option is chosen. Depending on browser
    // timing, that unmount can run before this handler, leaving the clicked node detached from
    // the DOM — at which point `closest`/`contains` can no longer see the menu ancestry. Treat a
    // detached target as an in-widget interaction rather than an outside click.
    const isDetachedTarget = Boolean(target) && target?.isConnected === false;

    if (!isClickedIgnoredElement && !isClickedPortalSurface && !isDetachedTarget) {
      const clickedElement = target;
      const parentElement = element.current;
      const hasClickedSelf = clickedElement === parentElement;
      const hasClickedChild = parentElement && parentElement.contains(clickedElement);
      const hasClickedOutside = !hasClickedSelf && !hasClickedChild && parentElement;
      if (hasClickedOutside && callback) {
        callback();
      }
    }
  };
  
  useEffect(() => {
    document.body.addEventListener('click', handler, { passive: true, capture: true });
    return () => document.body.removeEventListener('click', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [element, callback]);

  return element;
}
